import { useAccessibility } from "../../context/AccessibilityContext/AccessibilityContext";
import Modal from "../Modal/Modal";
import { useState } from 'react';
import style from './newsmodal.module.scss';

const NewsModal = ({ news, onClose }) => {
    const { settings: { contrast } } = useAccessibility();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

    if (!news) return null;

    // Accessibility-aware colors
    const backgroundColor = contrast === 'default' ? '#fff' : 'var(--bg-primary)';
    const textColor = contrast === 'default' ? '#231f20' : 'var(--text-primary)';
    const accentColor = contrast === 'default' ? '#1d3e8c' : 'var(--accent-color)';
    const borderColor = contrast === 'default' ? '#e5e5e5' : 'var(--border-color)';

    // Unified data access with fallbacks
    const getNewsData = () => {
        return {
            title: news.title || news.Title || 'Без названия',
            content: news.text || news.Text || news.content || news.description || '',
            image: news.image || news.Image || null,
            date: news.publishedAt || news.published_at || news.createdAt || news.created_at || news.date || null,
            type: news.type || news.ype || 'news'
        };
    };

    const newsData = getNewsData();
    const imageUrl = newsData.image ? `${newsData.image}` : null;

    const handleImageLoad = (e) => {
        setImageLoaded(true);
        setImageDimensions({
            width: e.target.naturalWidth,
            height: e.target.naturalHeight
        });
    };

    const handleImageError = (e) => {
        e.target.style.display = 'none';
        setImageLoaded(true);
    };

    // Calculate image height based on aspect ratio
    const getImageHeight = () => {
        if (!imageDimensions.width || !imageDimensions.height) return 'auto';
        
        const aspectRatio = imageDimensions.height / imageDimensions.width;
        const maxWidth = 800; // modal maxWidth
        return Math.min(maxWidth * aspectRatio, 500); // Limit max height to 500px
    };

    // Robust date formatting
    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        try {
            let cleanDate = dateString;
            
            if (cleanDate.includes('+')) {
                cleanDate = cleanDate.replace('+00:00', 'Z');
            } else if (!cleanDate.endsWith('Z') && cleanDate.includes('T')) {
                cleanDate += 'Z';
            }
            
            const date = new Date(cleanDate);
            
            if (isNaN(date.getTime())) {
                const datePart = cleanDate.split('T')[0];
                const simpleDate = new Date(datePart);
                
                if (!isNaN(simpleDate.getTime())) {
                    return simpleDate.toLocaleDateString('ru-RU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    });
                }
                return '';
            }
            
            return date.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch (error) {
            console.warn('Date formatting error:', error, 'Date:', dateString);
            return '';
        }
    };

    // Safe text content handling
    const getTextContent = () => {
        const { content } = newsData;
        
        if (!content) {
            return [<p key="no-content" className={style.newsParagraph}>Содержание недоступно</p>];
        }

        if (Array.isArray(content)) {
            return content
                .filter(paragraph => paragraph && paragraph.trim())
                .map((paragraph, index) => (
                    <p key={index} className={style.newsParagraph}>
                        {paragraph}
                    </p>
                ));
        }

        if (typeof content === 'string') {
            const paragraphs = content.split('\n').filter(para => para.trim());
            if (paragraphs.length === 0) {
                return [<p key="no-content" className={style.newsParagraph}>Содержание недоступно</p>];
            }
            
            return paragraphs.map((paragraph, index) => (
                <p key={index} className={style.newsParagraph}>
                    {paragraph}
                </p>
            ));
        }

        return [<p key="no-content" className={style.newsParagraph}>Содержание недоступно</p>];
    };

    const formattedDate = formatDate(newsData.date);

    return (
        <Modal
            active={!!news}
            setActive={onClose}
            width="90%"
            maxWidth="800px"
            height="90vh"
            maxHeight="90vh"
            padding="0"
        >
            <div 
                className={style.newsModalContent}
                style={{ 
                    background: backgroundColor,
                    color: textColor
                }}
            >
                {/* Close Button */}
                <button 
                    className={style.closeButton}
                    onClick={onClose}
                    aria-label="Закрыть"
                    style={{
                        color: textColor,
                        border: `2px solid ${borderColor}`,
                        background: backgroundColor
                    }}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Scrollable Container */}
                <div className={style.scrollableContainer}>
                    {/* Header with Image - Now shows full image */}
                    {imageUrl && (
                        <div className={style.newsHeader}>
                            <div 
                                className={style.imageContainer}
                                style={{ 
                                    height: imageLoaded ? getImageHeight() : 'auto',
                                    maxHeight: '70vh' // Don't let image take more than 70% of modal height
                                }}
                            >
                                <img 
                                    src={imageUrl} 
                                    alt={newsData.title} 
                                    className={style.newsImage}
                                    onLoad={handleImageLoad}
                                    onError={handleImageError}
                                />
                                {!imageLoaded && (
                                    <div className={style.imageLoader}>
                                        <div className={style.loaderSpinner}></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className={style.newsBody}>
                        {/* Meta Information */}
                        <div className={style.newsMeta}>
                            <span 
                                className={style.newsType}
                                style={{
                                    background: accentColor,
                                    color: '#fff'
                                }}
                            >
                                📰 Новость
                            </span>
                            {formattedDate && (
                                <span className={style.newsDate}>
                                    {formattedDate}
                                </span>
                            )}
                        </div>

                        {/* Title */}
                        <h1 className={style.newsTitle}>
                            {newsData.title}
                        </h1>

                        {/* Text Content */}
                        <div className={style.newsText}>
                            {getTextContent()}
                        </div>

                        {/* Footer */}
                        <div className={style.newsFooter}>
                            <button 
                                className={style.closeActionButton}
                                onClick={onClose}
                                style={{
                                    background: accentColor,
                                    color: '#fff'
                                }}
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default NewsModal